import { InputBase } from "@mui/material";
import { KeyboardEvent, useRef, useState } from "react";
import { useTranslation } from "../../../i18n";

type Props = {
  /** The page number shown until the reviewer types, and again after an edit ends. */
  page: number;
  /** Sizes the field so the longest page number fits. */
  pageCount: number;
  disabled: boolean;
  /**
   * Fires once per committed edit with the typed whole number, unclamped: keeping
   * it within the document is the caller's call. An empty edit commits nothing.
   */
  onCommit: (page: number) => void;
  onFocus?: () => void;
  onBlur?: () => void;
};

/** The current page as a field: Enter or blur commits what was typed, Escape discards it. */
export const PdfPageField = ({ page, pageCount, disabled, onCommit, onFocus, onBlur }: Props) => {
  const { t } = useTranslation("pdfPreview");
  const [draft, setDraft] = useState<string | null>(null);
  // Enter and Escape end the edit themselves rather than through the blur they cause:
  // a document without window focus fires no blur event. The blur that does follow
  // must not end the edit a second time.
  const endedByKeyRef = useRef(false);
  const digits = Math.max(String(pageCount).length, String(page).length);

  const endEdit = (commit: boolean) => {
    setDraft(null);
    if (commit && draft) onCommit(Number(draft));
  };

  const handleBlur = () => {
    onBlur?.();
    if (endedByKeyRef.current) {
      endedByKeyRef.current = false;
      return;
    }
    endEdit(true);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (event.key !== "Enter" && event.key !== "Escape") return;

    if (event.key === "Escape") {
      // Escape here only cancels the edit, not whatever else listens for it.
      event.stopPropagation();
    }
    endedByKeyRef.current = true;
    endEdit(event.key === "Enter");
    event.currentTarget.blur();
  };

  return (
    <InputBase
      value={draft ?? String(page)}
      disabled={disabled}
      onChange={(event) => setDraft(event.target.value.replace(/\D/g, ""))}
      onFocus={(event) => {
        endedByKeyRef.current = false;
        event.target.select();
        onFocus?.();
      }}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      inputProps={{ inputMode: "numeric", "aria-label": t("pageNumber") }}
      sx={{
        typography: "body2",
        color: "inherit",
        "& .MuiInputBase-input": {
          boxSizing: "content-box",
          width: `${digits}ch`,
          height: "auto",
          px: 0.5,
          py: 0.25,
          textAlign: "center",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 0.5,
        },
        "& .MuiInputBase-input:hover:not(:disabled)": { borderColor: "text.secondary" },
        "&.Mui-focused .MuiInputBase-input": { borderColor: "primary.main" },
      }}
    />
  );
};
